import Highcharts, { Options, SeriesLineOptions } from 'highcharts/highstock';
import HighchartsReact from 'highcharts-react-official';
import { format } from 'date-fns';
import { useEffect, useState } from 'react';
import {
    deployedColor,
    shippedColor,
    sleepColor,
    retiredColor,
} from 'store/services/device-status';
import { StatusTrendGraphSeries } from 'types';

const defaultOptions: Options = {
    title: { text: 'Status Trend' },
    chart: {
        type: 'line',
        height: 300,
    },
    tooltip: {
        split: true,
        formatter: function () {
            // The first returned item is the x value timestamp, subsequent items are the
            // y data points
            return [
                this.x
                    ? '<b>' +
                      format(new Date(this.x), 'M/d/yyyy h:mm aaa') +
                      '</b>'
                    : '',
            ].concat(
                this.points
                    ? this.points.map(function (point) {
                          return (
                              point.series.name +
                              ': ' +
                              point.y?.toLocaleString()
                          );
                      })
                    : []
            );
        },
    },
    xAxis: {
        title: { text: 'Date' },
        type: 'datetime',
        ordinal: false,
    },
    yAxis: {
        title: { text: '# of Monitors' },
    },
    credits: { enabled: false },
    rangeSelector: {
        selected: 1,
        buttonSpacing: 5,
        buttonTheme: {
            width: 80,
        },
        enabled: true,
        allButtonsEnabled: true,
        buttons: [
            {
                type: 'month',
                count: 1,
                text: '1 month',
            },
            {
                type: 'month',
                count: 3,
                text: '3 months',
            },
            {
                type: 'month',
                count: 6,
                text: '6 months',
            },
            {
                type: 'ytd',
                text: 'Year to date',
            },
            {
                type: 'year',
                count: 1,
                text: '1 year',
            },
            {
                type: 'year',
                count: 2,
                text: '2 years',
            },
            {
                type: 'all',
                text: 'Show All',
            },
        ],
    },
    series: [],
};

export interface StatusTrendGraphProps {
    highcharts?: typeof Highcharts;
    statusTrendGraph?: StatusTrendGraphSeries;
}

/** Historical trend line graph showing number of devices in each status on each date. */
export const StatusTrendGraph = ({
    highcharts,
    statusTrendGraph,
}: StatusTrendGraphProps) => {
    const [options, setOptions] = useState<Options>({});

    // if anyone can figure out how to enable accessibility for Highcharts in TypeScript, please do so
    Highcharts.defaultOptions.accessibility = {
        enabled: false,
    };

    if (!!highcharts) {
        highcharts.defaultOptions.accessibility = {
            enabled: false,
        };
    }

    // Build highcharts graph series object from trend data.
    useEffect(() => {
        if (statusTrendGraph) {
            const updatedSeries: SeriesLineOptions[] = [];

            updatedSeries.push({
                id: 'deployed',
                name: 'Deployed',
                color: deployedColor,
                type: 'line',
                data: statusTrendGraph.deployed.data,
            });

            updatedSeries.push({
                id: 'shipped',
                name: 'Shipped',
                color: shippedColor,
                type: 'line',
                data: statusTrendGraph.shipped.data,
            });

            updatedSeries.push({
                id: 'sleep',
                name: 'Sleep',
                color: sleepColor,
                type: 'line',
                data: statusTrendGraph.sleep.data,
            });

            // There will only be retired monitor data for internal users.
            if (statusTrendGraph.retired) {
                updatedSeries.push({
                    id: 'retired',
                    name: 'Retired',
                    color: retiredColor,
                    type: 'line',
                    data: statusTrendGraph.retired.data,
                });
            }

            defaultOptions.series = updatedSeries;
            setOptions(defaultOptions);
        }
    }, [statusTrendGraph]);

    return (
        <div data-testid="StatusTrendGraph">
            <HighchartsReact
                highcharts={highcharts ?? Highcharts}
                options={options}
            />
        </div>
    );
};
